import React, { useRef } from 'react';
import { Panels } from './CosmeticRepairRequestTypes';
import SelectCarTop from '../../../images/car_top.png';
import SelectCarBottom from '../../../images/car_bottom.png';
import SelectCarMiddle from '../../../images/car_middle.png';
import SelectCarRight from '../../../images/car_right.png';
import SelectCarLeft from '../../../images/car_left.png';
import './SVGPanelSelector.scss';
import { useTranslation } from 'react-i18next';
import usePanelSelector from './usePanelSelector';

export type SVGPanelSelectorProps = {
    selectedPanels: Panels[];
    allowedPanels: Panels[];
    notAllowedSelectionMessage?: string;
    onSelectionChange: (selected: boolean, panel: Panels) => void;
};

const SVGPanelSelector = ({
    selectedPanels,
    allowedPanels,
    notAllowedSelectionMessage,
    onSelectionChange
}: SVGPanelSelectorProps): JSX.Element => {
    const { t } = useTranslation();
    const containerRef = useRef<HTMLDivElement | null>(null);

    const {
        getPanelClass,
        handlePanelLeave,
        handleSelectionChange,
        notAllowedSelectionTooltip
    } = usePanelSelector({
        selectedPanels,
        allowedPanels,
        notAllowedSelectionMessage,
        containerRef,
        onSelectionChange
    });

    return (
        <div
            ref={containerRef}
            style={{ height: 1000, width: 450, position: 'relative' }}>
            <svg style={{ height: 200, width: 450 }}>
                <defs>
                    <pattern
                        id='img1'
                        patternUnits='userSpaceOnUse'
                        x='-60'
                        y='0'
                        width='450'
                        height='200'>
                        <image href={SelectCarTop} width='450' height='200' />
                    </pattern>
                </defs>
                <path
                    d='M1,1
        5,580 l980,0 5,-580 l 0,0'
                    fill='url(#img1)'
                />
                <path
                    d='m93,92.5c0,0 110,1 110,0.5c0,0.5 1,-9.5 1,-10c0,0.5 -112,1.5 -112,1c0,0.5 1,8.5 1,8.5z'
                    data-testid='driver-sill'
                    className={getPanelClass(Panels.DriverSill)}
                    onClick={(e) => {
                        handleSelectionChange(e, Panels.DriverSill);
                    }}
                    onMouseLeave={handlePanelLeave}>
                    <title>{t('driver_sill')}</title>
                </path>
                <path
                    d='m32,85.5c0,0 9,27.98785 9,27.53644c0,0.45142 -15,0.45142 -15,0c0,0.45142 -14,-10.38259 -14,-10.83401c0,0.45142 1,-14.89676 1,-15.34818c0,0.45142 19,-1.35425 19,-1.35425z'
                    data-testid='front-bumper'
                    className={getPanelClass(Panels.FrontBumper)}
                    onClick={(e) => {
                        handleSelectionChange(e, Panels.FrontBumper);
                    }}
                    onMouseLeave={handlePanelLeave}>
                    <title>{t('front_bumper')}</title>
                </path>
                <path
                    d='m10,105.5c0,0 17,10.17044 17,9.70815c0,0.46229 60,6.93439 60,6.4721c0,0.46229 -7,8.78356 -7,8.32127c0,0.46229 -56,-8.78356 -56,-9.24585c0,0.46229 -14,-15.25566 -14,-15.25566z'
                    data-testid='bonnet'
                    className={getPanelClass(Panels.Bonnet)}
                    onClick={(e) => {
                        handleSelectionChange(e, Panels.Bonnet);
                    }}
                    onMouseLeave={handlePanelLeave}>
                    <title>{t('bonnet')}</title>
                </path>
                <path
                    d='m95,93.4737c0,0 0,27.47448 0,27.00078c0,0.4737 29,21.3164 29,21.3164c0,0 36,5.68438 36,5.21068c0,0.4737 -5,-53.52786 -5,-54.00156c0,0.4737 -60,0.4737 -60,0.4737z'
                    data-testid='front-driver-door'
                    className={getPanelClass(Panels.FrontDriverDoor)}
                    onClick={(e) => {
                        handleSelectionChange(e, Panels.FrontDriverDoor);
                    }}
                    onMouseLeave={handlePanelLeave}>
                    <title>{t('front_driver_door')}</title>
                </path>
                <path
                    d='m156,93.49107l5,53.5268l36,0.98215l19,-24.55357l-16,-30.44643l-44,0.49107l0,-0.00001l0,-0.00001z'
                    data-testid='rear-driver-door'
                    className={getPanelClass(Panels.RearDriverDoor)}
                    onClick={(e) => {
                        handleSelectionChange(e, Panels.RearDriverDoor);
                    }}
                    onMouseLeave={handlePanelLeave}>
                    <title>{t('rear_driver_door')}</title>
                </path>
                <path
                    d='m127,147.5c0,0 68,1.0744 68,0.89533c0,0.17907 0,4.83481 0,4.65574c0,0.17907 -66,-0.5372 -66,-0.71627c0,0.17907 -2,-4.83481 -2,-4.83481l0,0.00001z'
                    data-testid='roof'
                    className={getPanelClass(Panels.Roof)}
                    onClick={(e) => {
                        handleSelectionChange(e, Panels.Roof);
                    }}
                    onMouseLeave={handlePanelLeave}>
                    <title>{t('roof')}</title>
                </path>
                <path
                    d='m271,113.5c0,0 -1,16 -1,15.5c0,0.5 -15,4.5 -15,4c0,0.5 31,0.5 31,0c0,0.5 3,-21.5 3,-22c0,0.5 -18,2.5 -18,2.5z'
                    data-testid='boot'
                    className={getPanelClass(Panels.Boot)}
                    onClick={(e) => {
                        handleSelectionChange(e, Panels.Boot);
                    }}
                    onMouseLeave={handlePanelLeave}>
                    <title>{t('boot')}</title>
                </path>
                <path
                    d='m249,97.5c0,0 21,1 21,0.5c0,0.5 1,13.5 1,13c0,0.5 20,-1.5 20,-2c0,0.5 -4,-19.5 -4,-20c0,0.5 -39,-1.5 -39,-2c0,0.5 1,10.5 1,10.5z'
                    data-testid='rear-bumper'
                    className={getPanelClass(Panels.RearBumper)}
                    onClick={(e) => {
                        handleSelectionChange(e, Panels.RearBumper);
                    }}
                    onMouseLeave={handlePanelLeave}>
                    <title>{t('rear_bumper')}</title>
                </path>
                <path
                    d='m50,83.5c0,0 -18,2 -18,1.5c0,0.5 10,29.5 10,29c0,0.5 44,8.5 44,8c0,0.5 7,-2.5 7,-3c0,0.5 -1,-34.5 -1,-35c0,0.25 -1.75,0.625 -3.5,0.875c-1.75,0.25 -3.5,0.375 -3.5,0.125c11,38.5 -52,36.5 -35,-1.5z'
                    data-testid='front-driver-wing'
                    className={getPanelClass(Panels.FrontDriverWing)}
                    onClick={(e) => {
                        handleSelectionChange(e, Panels.FrontDriverWing);
                    }}
                    onMouseLeave={handlePanelLeave}>
                    <title>{t('front_driver_wing')}</title>
                </path>
                <path
                    d='m204,95.5l13,30.5l18,0l-35,20l56,-14l12,-4l3,-28l-22,-1c1,18.83333 -39,23.66667 -45,-3.5z'
                    data-testid='rear-driver-wing'
                    className={getPanelClass(Panels.RearDriverWing)}
                    onClick={(e) => {
                        handleSelectionChange(e, Panels.RearDriverWing);
                    }}
                    onMouseLeave={handlePanelLeave}>
                    <title>{t('rear_driver_wing')}</title>
                </path>
            </svg>
            <svg style={{ height: 200, width: 300 }}>
                <defs>
                    <pattern
                        id='img2'
                        patternUnits='userSpaceOnUse'
                        x='10'
                        y='0'
                        width='300'
                        height='200'>
                        <image href={SelectCarLeft} width='300' height='200' />
                    </pattern>
                </defs>
                <path
                    d='M3,3
        l0,680 l980,0 l0,-680 l-980,0'
                    fill='url(#img2)'
                />
                <path
                    d='m172,33.5c0,0 -1,139 -1,138.5c0,0.5 14,-4.5 14,-5c0,0.5 2,-20.5 2,-21c0,0.5 -1,-88.5 -1,-89c0,0.5 -2,-15.5 -2,-16c0,0.5 -12,-7.5 -12,-7.5z'
                    data-testid='bonnet'
                    className={getPanelClass(Panels.Bonnet)}
                    onClick={(e) => {
                        handleSelectionChange(e, Panels.Bonnet);
                    }}
                    onMouseLeave={handlePanelLeave}>
                    <title>{t('bonnet')}</title>
                </path>
                <path
                    d='m131.96898,38.14448c0,0 38.48622,-5.14336 38.01569,-5.64765c0.47053,0.50429 -0.99098,143.48967 -1.4615,142.98537c0.47053,0.50429 -33.63118,-4.12473 -34.10172,-4.62902c0.47053,0.50429 -2.45247,-132.7087 -2.45247,-132.7087z'
                    data-testid='front-bumper'
                    className={getPanelClass(Panels.FrontBumper)}
                    onClick={(e) => {
                        handleSelectionChange(e, Panels.FrontBumper);
                    }}
                    onMouseLeave={handlePanelLeave}>
                    <title>{t('front_bumper')}</title>
                </path>
                <path
                    d='m214,58.5c0,0 9,-3 9,-3.5c0,0.5 1,93.5 1,93c0,0.5 -8,0.5 -8,0c0,0.5 -2,-89.5 -2,-89.5z'
                    data-testid='roof'
                    className={getPanelClass(Panels.Roof)}
                    onClick={(e) => {
                        handleSelectionChange(e, Panels.Roof);
                    }}
                    onMouseLeave={handlePanelLeave}>
                    <title>{t('roof')}</title>
                </path>
            </svg>
            <svg style={{ height: 200, width: 300 }}>
                <defs>
                    <pattern
                        id='img3'
                        patternUnits='userSpaceOnUse'
                        x='0'
                        y='0'
                        width='300'
                        height='200'>
                        <image
                            href={SelectCarMiddle}
                            width='300'
                            height='200'
                        />
                    </pattern>
                </defs>
                <path
                    d='M3,3
            l0,680 l980,0 l0,-680 l-980,0'
                    fill='url(#img3)'
                />
                <path
                    d='m32.53002,59.2075l62.5,0c-6.90356,0 -12.5,19.25176 -12.5,43c0,23.74825 5.59644,43 12.5,43l-62.5,0l0,0c-6.90356,0 -12.5,-19.25176 -12.5,-43c0,-23.74824 5.59644,-43 12.5,-43z'
                    data-testid='bonnet'
                    className={getPanelClass(Panels.Bonnet)}
                    onClick={(e) => {
                        handleSelectionChange(e, Panels.Bonnet);
                    }}
                    onMouseLeave={handlePanelLeave}>
                    <title>{t('bonnet')}</title>
                </path>
                <path
                    d='m36,57.5c0,0 -18,20 -18,19.5c0,0.5 -1,43.5 -1,43c0,0.5 21,29.5 21,29c0,0.5 -7,1.5 -7,1c0,0.5 -20,-22.5 -20,-23c0,0.5 -3,-47.5 -3,-48c0,0.5 21,-23.5 21,-24c0,0.5 7,2.5 7,2.5z'
                    data-testid='front-bumper'
                    className={getPanelClass(Panels.FrontBumper)}
                    onClick={(e) => {
                        handleSelectionChange(e, Panels.FrontBumper);
                    }}
                    onMouseLeave={handlePanelLeave}>
                    <title>{t('front_bumper')}</title>
                </path>
                <path
                    d='m128,69.5c0,0 0,64 0,63.5c0,0.5 88,-0.5 88,-1c0,0.5 -1.03529,-61.5 -1.03529,-62c0,0.5 -86.96471,-0.5 -86.96471,-0.5z'
                    data-testid='roof'
                    className={getPanelClass(Panels.Roof)}
                    onClick={(e) => {
                        handleSelectionChange(e, Panels.Roof);
                    }}
                    onMouseLeave={handlePanelLeave}>
                    <title>{t('roof')}</title>
                </path>
                <path
                    d='m253,69.5c0,0 1,68 1,67.5c0,0.5 22,0.5 22,0c0,0.5 9,-16.5 9,-17c0,0.5 1,-30.5 1,-31c0,0.5 -9,-20.5 -9,-21c0,0.5 -24,1.5 -24,1.5z'
                    data-testid='boot'
                    className={getPanelClass(Panels.Boot)}
                    onClick={(e) => {
                        handleSelectionChange(e, Panels.Boot);
                    }}
                    onMouseLeave={handlePanelLeave}>
                    <title>{t('boot')}</title>
                </path>
                <path
                    d='m94,51.5c0,0 5,7 5,6.5c0,0.5 30,11.5 30,11c0,0.5 35,-0.5 35,-1c0,0.5 -7,-17.5 -7,-18c0,0.5 -63,1.5 -63,1.5z'
                    data-testid='front-driver-door'
                    className={getPanelClass(Panels.FrontDriverDoor)}
                    onClick={(e) => {
                        handleSelectionChange(e, Panels.FrontDriverDoor);
                    }}
                    onMouseLeave={handlePanelLeave}>
                    <title>{t('front_driver_door')}</title>
                </path>
                <path
                    d='m159,49.5c0,0 7,20 7,19.5c0,0.5 35,-1.5 35,-2c0,0.5 16,-10.5 16,-11c0,0.5 -9,-4.5 -9,-5c0,0.5 -49,-1.5 -49,-1.5z'
                    data-testid='rear-driver-door'
                    className={getPanelClass(Panels.RearDriverDoor)}
                    onClick={(e) => {
                        handleSelectionChange(e, Panels.RearDriverDoor);
                    }}
                    onMouseLeave={handlePanelLeave}>
                    <title>{t('rear_driver_door')}</title>
                </path>
                <path
                    d='m95,154.5c0,0 3,-8 3,-8.5c0,0.5 28,-10.5 28,-11c0,0.5 37,0.5 37,0c0,0.5 -7,20.5 -7,20c0,0.5 -61,-0.5 -61,-0.5z'
                    data-testid='front-passenger-door'
                    className={getPanelClass(Panels.FrontPassengerDoor)}
                    onClick={(e) => {
                        handleSelectionChange(e, Panels.FrontPassengerDoor);
                    }}
                    onMouseLeave={handlePanelLeave}>
                    <title>{t('front_passenger_door')}</title>
                </path>
                <path
                    d='m165,135.5c0,0 36,-1 36,-1.5c0,0.5 16,11.5 16,11c0,0.5 -8,9.5 -8,9c0,0.5 -51,-0.5 -51,-1c0,0.5 7,-17.5 7,-17.5z'
                    data-testid='rear-passenger-door'
                    className={getPanelClass(Panels.RearPassengerDoor)}
                    onClick={(e) => {
                        handleSelectionChange(e, Panels.RearPassengerDoor);
                    }}
                    onMouseLeave={handlePanelLeave}>
                    <title>{t('rear_passenger_wing')}</title>
                </path>
                <path
                    d='m204,134.5c0,0 14,11 14,10.5c0,0.5 -9,10.5 -9,10c0,0.5 38,0.5 38,0c0,0.5 7,-4.5 7,-5c0,0.5 20,-2.5 20,-3c0,0.5 1,-9.5 1,-10c0,0.5 -71,-2.5 -71,-2.5z'
                    data-testid='rear-passenger-wing'
                    className={getPanelClass(Panels.RearPassengerWing)}
                    onClick={(e) => {
                        handleSelectionChange(e, Panels.RearPassengerWing);
                    }}
                    onMouseLeave={handlePanelLeave}>
                    <title>{t('rear_passenger_wing')}</title>
                </path>
                <path
                    d='m208,50.5l8,6.5l-16,11l76,0l-1,-11l-22,-4l-7,-4l-38,1.5z'
                    data-testid='rear-driver-wing'
                    className={getPanelClass(Panels.RearDriverWing)}
                    onClick={(e) => {
                        handleSelectionChange(e, Panels.RearDriverWing);
                    }}
                    onMouseLeave={handlePanelLeave}>
                    <title>{t('rear_driver_wing')}</title>
                </path>
                <path
                    d='m43,150.5c0,0 52,-3 52,-3.5c0,0.5 2,7.5 2,7c0,0.5 -47,2.5 -47,2c0,0.5 -7,-5.5 -7,-5.5z'
                    data-testid='front-passenger-wing'
                    className={getPanelClass(Panels.FrontPassengerWing)}
                    onClick={(e) => {
                        handleSelectionChange(e, Panels.FrontPassengerWing);
                    }}
                    onMouseLeave={handlePanelLeave}>
                    <title>{t('front_passenger_wing')}</title>
                </path>
                <path
                    d='m41,56.5c0,0 55,0 55,-0.5c0,0.5 -3,-6.5 -3,-7c0,0.5 -47,0.5 -47,0c0,0.5 -5,7.5 -5,7.5z'
                    data-testid='front-driver-wing'
                    className={getPanelClass(Panels.FrontDriverWing)}
                    onClick={(e) => {
                        handleSelectionChange(e, Panels.FrontDriverWing);
                    }}
                    onMouseLeave={handlePanelLeave}>
                    <title>{t('front_driver_wing')}</title>
                </path>
            </svg>
            <svg style={{ height: 200, width: 300 }}>
                <defs>
                    <pattern
                        id='img4'
                        patternUnits='userSpaceOnUse'
                        x='0'
                        y='0'
                        width='300'
                        height='200'>
                        <image href={SelectCarRight} width='300' height='200' />
                    </pattern>
                </defs>
                <path
                    d='M3,3
            l0,680 l980,0 l0,-680 l 0,0'
                    fill='url(#img4)'
                />
                <path
                    d='m164,26.5c0,0 -1,149 -1,148.5c0,0.5 18,-1.5 18,-2c0,0.5 0,-140.5 0,-141c0,0.5 -17,-5.5 -17,-5.5z'
                    data-testid='rear-bumper'
                    className={getPanelClass(Panels.RearBumper)}
                    onClick={(e) => {
                        handleSelectionChange(e, Panels.RearBumper);
                    }}
                    onMouseLeave={handlePanelLeave}>
                    <title>{t('rear_bumper')}</title>
                </path>
                <path
                    d='m162,34.5c0,0 -36,2 -36,1.5c0,0.5 -6,16.5 -6,16c0,0.5 -1,95.5 -1,95c0,0.5 9,16.5 9,16c0,0.5 33,3.5 33,3c0,0.5 1,-131.5 1,-131.5z'
                    data-testid='boot'
                    className={getPanelClass(Panels.Boot)}
                    onClick={(e) => {
                        handleSelectionChange(e, Panels.Boot);
                    }}
                    onMouseLeave={handlePanelLeave}>
                    <title>{t('boot')}</title>
                </path>
                <path
                    d='m87,54.5c0,0 31,-5 31,-5.5c0,0.5 8,-10.5 8,-11c0,0.5 39,-2.5 39,-3c0,0.5 1,-9.5 1,-10c0,0.5 -32,0.5 -32,0c0,0.5 -18,9.5 -18,9c0,0.5 -29,20.5 -29,20.5z'
                    data-testid='rear-driver-wing'
                    className={getPanelClass(Panels.RearDriverWing)}
                    onClick={(e) => {
                        handleSelectionChange(e, Panels.RearDriverWing);
                    }}
                    onMouseLeave={handlePanelLeave}>
                    <title>{t('rear_driver_wing')}</title>
                </path>
                <path
                    d='m87,148.5c0,0 32,5 32,4.5c0,0.5 6,8.5 6,8c0,0.5 40,5.5 40,5c0,0.5 7,6.5 7,6c0,0.5 -38,5.5 -38,5c0,0.5 -17,-10.5 -17,-11c0,0.5 -30,-17.5 -30,-17.5z'
                    data-testid='rear-passenger-wing'
                    className={getPanelClass(Panels.RearPassengerWing)}
                    onClick={(e) => {
                        handleSelectionChange(e, Panels.RearPassengerWing);
                    }}
                    onMouseLeave={handlePanelLeave}>
                    <title>{t('rear_passenger_wing')}</title>
                </path>
                <path
                    d='m87,53.5c0,0 7,2 7,1.5c0,0.5 0,92.5 0,92c0,0.5 -7,0.5 -7,0c0,0.5 0,-93.5 0,-93.5z'
                    data-testid='roof'
                    className={getPanelClass(Panels.Roof)}
                    onClick={(e) => {
                        handleSelectionChange(e, Panels.Roof);
                    }}
                    onMouseLeave={handlePanelLeave}>
                    <title>{t('roof')}</title>
                </path>
            </svg>
            <svg style={{ height: 200, width: 450 }}>
                <defs>
                    <pattern
                        id='img5'
                        patternUnits='userSpaceOnUse'
                        x='-60'
                        y='0'
                        width='450'
                        height='200'>
                        <image
                            href={SelectCarBottom}
                            width='450'
                            height='200'
                        />
                    </pattern>
                </defs>
                <path
                    d='M3,3
            l0,680 l980,0 l0,-680 l-980,0'
                    fill='url(#img5)'
                />
                <path
                    d='m92.99981,87.68932c0,0 -55.94934,4.35449 -56.48718,4.01957c0.53785,0.33492 -14.52544,-0.335 -15.06328,-0.66992c0.53785,0.33492 -8.06974,6.02926 -8.60759,5.69433c0.53785,0.33492 -4.30393,0.66988 -4.84177,0.33496c0.53785,0.33492 5.91758,-6.69925 5.91758,-6.69925c0,0 7.53164,-2.00977 6.9938,-2.34469c0.53785,0.33492 60.79097,-5.02445 60.79097,-5.02445c0,0 11.29746,4.68945 11.29746,4.68945l0.00001,0.00001l0,-0.00001z'
                    data-testid='bonnet'
                    className={getPanelClass(Panels.Bonnet)}
                    onClick={(e) => {
                        handleSelectionChange(e, Panels.Bonnet);
                    }}
                    onMouseLeave={handlePanelLeave}>
                    <title>{t('bonnet')}</title>
                </path>
                <path
                    d='m98,88.01852l37.29032,-19.87036l30.70968,-2.14815l-4.3871,58l-63.6129,-1.07407l0,-34.90741l0,-0.00001z'
                    data-testid='front-passenger-door'
                    className={getPanelClass(Panels.FrontPassengerDoor)}
                    onClick={(e) => {
                        handleSelectionChange(e, Panels.FrontPassengerDoor);
                    }}
                    onMouseLeave={handlePanelLeave}>
                    <title>{t('front_driver_door')}</title>
                </path>
                <path
                    d='m163,123.45183c0,0 4.77193,-55.91419 4.77193,-56.45183c0,0.53763 45.33333,0.53763 45.33333,0c0,0.53763 17.89474,19.89255 17.89474,19.35491c0,0.53763 -21.47368,38.17219 -21.47368,37.63455c0,0.53763 -46.52631,-0.53763 -46.52631,-0.53763l-0.00001,-0.00001l0,0.00001z'
                    data-testid='rear-passenger-door'
                    className={getPanelClass(Panels.RearPassengerDoor)}
                    onClick={(e) => {
                        handleSelectionChange(e, Panels.RearPassengerDoor);
                    }}
                    onMouseLeave={handlePanelLeave}>
                    <title>{t('rear_passenger_wing')}</title>
                </path>
                <path
                    d='m19.125,92.67647l21.875,-1.67647l-12.03125,11.17647l0,26.82353l-22.96875,0l0,-26.82353l13.125,-9.5z'
                    data-testid='front-bumper'
                    className={getPanelClass(Panels.FrontBumper)}
                    onClick={(e) => {
                        handleSelectionChange(e, Panels.FrontBumper);
                    }}
                    onMouseLeave={handlePanelLeave}>
                    <title>{t('front_bumper')}</title>
                </path>
                <path
                    d='m95,123.5c0,0 126,-2 126,-2.5c0,0.5 3,10.5 3,10c0,0.5 -134,1.5 -134,1c0,0.5 5,-8.5 5,-8.5z'
                    data-testid='passenger-sill'
                    className={getPanelClass(Panels.PassengerSill)}
                    onClick={(e) => {
                        handleSelectionChange(e, Panels.PassengerSill);
                    }}
                    onMouseLeave={handlePanelLeave}>
                    <title>{t('passenger_sill')}</title>
                </path>
                <path
                    d='m293.3,102.5c0,0 -3.6,10.52963 -3.6,9.94465c0,0.58497 -20.7,1.75493 -20.7,1.16996c0,0.58497 1.8,16.9644 1.8,16.37943c0,0.58497 36,-5.26481 36,-5.8498c0,0.58497 7.2,-20.47428 7.2,-21.05927c0,0.58497 -20.7,-0.58497 -20.7,-0.58497z'
                    data-testid='rear-bumper'
                    className={getPanelClass(Panels.RearBumper)}
                    onClick={(e) => {
                        handleSelectionChange(e, Panels.RearBumper);
                    }}
                    onMouseLeave={handlePanelLeave}>
                    <title>{t('rear_bumper')}</title>
                </path>
                <path
                    d='m272,79.44231l22,1.44231l0,22.11538l19,0l-5,-25l-36,1.44231z'
                    data-testid='boot'
                    className={getPanelClass(Panels.Boot)}
                    onClick={(e) => {
                        handleSelectionChange(e, Panels.Boot);
                    }}
                    onMouseLeave={handlePanelLeave}>
                    <title>{t('boot')}</title>
                </path>
                <path
                    d='m136.94624,65.5c0,0 -0.94624,-5.51515 -0.94624,-5.71212c0,0.19697 86.10753,-0.59091 86.10753,-0.78788c0,0.19697 1.89247,5.71212 1.89247,5.51515c0,0.19697 -87.05376,0.98485 -87.05376,0.98485z'
                    data-testid='roof'
                    className={getPanelClass(Panels.Roof)}
                    onClick={(e) => {
                        handleSelectionChange(e, Panels.Roof);
                    }}
                    onMouseLeave={handlePanelLeave}>
                    <title>{t('roof')}</title>
                </path>
                <path
                    d='m43,131.5l-15,-2.5l2,-24l8,-12l57,-5l1,33l-8,13c3.5,-28.41666 -16,-31.83333 -23.5,-32.25c-7.5,-0.41667 -27,7.16666 -21.5,29.75z'
                    data-testid='front-passenger-wing'
                    className={getPanelClass(Panels.FrontPassengerWing)}
                    onClick={(e) => {
                        handleSelectionChange(e, Panels.FrontPassengerWing);
                    }}
                    onMouseLeave={handlePanelLeave}>
                    <title>{t('front_passenger_wing')}</title>
                </path>
                <path
                    d='m225,112.5c0,0 -7,0 -7,-0.5c0,0.5 14,-28.5 14,-29c0,0.5 20,-0.5 20,-1c0,0.5 -27,-16.5 -27,-17c0,0.5 7,-2.5 7,-3c0,0.5 43,19.5 43,19c0,0.5 17,0.5 17,0c0,0.5 -1,31.5 -1,31c0,0.5 -24,1.5 -24,1c-9,-24.5 -37,-21.5 -42,-0.5z'
                    data-testid='rear-passenger-wing'
                    className={getPanelClass(Panels.RearPassengerWing)}
                    onClick={(e) => {
                        handleSelectionChange(e, Panels.RearPassengerWing);
                    }}
                    onMouseLeave={handlePanelLeave}>
                    <title>{t('rear_passenger_wing')}</title>
                </path>
            </svg>
            {notAllowedSelectionTooltip && (
                <div
                    className='panel-selector-tooltip'
                    role='tooltip'
                    style={{
                        left: notAllowedSelectionTooltip.left,
                        top: notAllowedSelectionTooltip.top
                    }}>
                    {notAllowedSelectionTooltip.text}
                </div>
            )}
        </div>
    );
};

export default SVGPanelSelector;
